import type { MutableRefObject } from 'react';
import { createTokenCredential } from '@/api/endpoints';
import type { ScmConnection } from '@/api/schemas';
import { nextAttempt, type Attempt } from '@/features/integrations/credentialModel';
import { connectionLabel, scmBody, type ScmValues } from './scmConnectionModel';

type ScmBody = ReturnType<typeof scmBody>;

interface ConnectDeps {
  values: ScmValues;
  /** The id of the token an earlier attempt already stored: attached instead of stored twice. */
  stored: string | null;
  attempt: MutableRefObject<Attempt | null>;
  create: (v: { body: ScmBody; idempotencyKey: string }) => Promise<ScmConnection>;
  /** The token was stored: the form keeps its id (never the token) and clears the field. */
  onStored: (credentialId: string) => void;
}

async function storeToken(values: ScmValues, onStored: ConnectDeps['onStored']): Promise<string> {
  const credential = await createTokenCredential('bitbucket_access_token', `${connectionLabel(values)} token`, values.token.trim(), crypto.randomUUID());
  onStored(credential.id);
  return credential.id;
}

/** Stores the token once (`POST /credentials`), then creates the connection that references it. */
export async function connectScmConnection({ values, stored, attempt, create, onStored }: ConnectDeps): Promise<ScmConnection> {
  const credentialId = stored || (await storeToken(values, onStored));
  const body = scmBody(values, credentialId);
  attempt.current = nextAttempt(attempt.current, body);
  return create({ body, idempotencyKey: attempt.current.key });
}
